/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import EditForm from './EditForm'
import { toast } from '../../../../services/toast'
import { create, getById, update, getAvailableTypes } from '../../../../services/api/metadata'
import { usersApiService } from '../../../../services/api/users'

interface ModalEditProps {
  property?: any
  onClose: () => void
  onSuccess?: () => void
}

const ModalEdit: React.FC<ModalEditProps> = ({ property, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableContext = useContext(RcmTableDataContext)

  const [isEdit, setIsEdit] = useState(!!property)
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState<Promise<any> | null>(null)
  const [propertyData, setPropertyData] = useState<any>({})
  const [users, setUsers] = useState<any[]>([])
  const [groups, setGroups] = useState<any[]>([])
  const [availableTypes, setAvailableTypes] = useState<any[]>([])

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      // Load available types from API
      const types = getAvailableTypes(translate)
      setAvailableTypes(
        types.map((type: any) => ({
          ...type,
          value: type.id,
          label: type.value
        }))
      )

      // Load users and groups
      const usersGroupsData = await usersApiService.getAssembledUsersGroups()

      setUsers(
        usersGroupsData.users.map((user: any) => ({
          value: user.id,
          label: user.chName
        }))
      )

      setGroups(
        usersGroupsData.groups.map((group: any) => ({
          value: group.id,
          label: group.name
        }))
      )

      // Load property data if editing
      if (isEdit && property?.id) {
        const promise = getById(property.id)
        setLoading(promise)
        const data = await promise
        setPropertyData(data)
        setLoading(null)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      setLoading(null)
    }
  }

  const doSave = async (propertyValues: any) => {
    try {
      let apiCall: Promise<any>

      if (isEdit) {
        apiCall = update(propertyValues.id, propertyValues)
      } else {
        apiCall = create(propertyValues)
      }

      setLoading(apiCall)

      await apiCall

      toast.success(translate(isEdit ? 'METADATA_UPDATE_SUCCESS_MSG' : 'METADATA_CREATE_SUCCESS_MSG'))

      // Refresh the table
      if (tableContext?.refreshTable) {
        tableContext.refreshTable()
      }

      if (onSuccess) {
        onSuccess()
      }

      doClose()
    } catch (error: any) {
      console.error('Error saving property:', error)
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')

      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }

      toast.error(errorMsg)
    } finally {
      setLoading(null)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(isEdit ? 'METADATA_EDIT_METADATA' : 'METADATA_ADD_METADATA')}
      size="md"
      dataTestId="edit-property-dialog"
    >
      <EditForm
        onSubmit={doSave}
        doClose={doClose}
        isEdit={isEdit}
        property={propertyData}
        availableTypes={availableTypes}
        users={users}
        groups={groups}
      />
    </Modal>
  )
}

export default ModalEdit
